import { useMemo } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface TimePickerFieldProps {
  value: string; // "HH:MM", 24-hour
  onChange: (value: string) => void;
  stepMinutes?: number;
  className?: string;
}

function buildTimeOptions(step: number): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  for (let m = 0; m < 24 * 60; m += step) {
    const h = Math.floor(m / 60);
    const min = m % 60;
    const value = `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
    const period = h < 12 ? "AM" : "PM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    const label = `${h12}:${String(min).padStart(2, "0")} ${period}`;
    options.push({ value, label });
  }
  return options;
}

/** Themed time-of-day dropdown used in place of native <input type="time">,
 *  whose picker UI is rendered by the OS/browser and can't be restyled. */
export function TimePickerField({ value, onChange, stepMinutes = 15, className }: TimePickerFieldProps) {
  const options = useMemo(() => buildTimeOptions(stepMinutes), [stepMinutes]);
  // If the current value falls off the step grid (e.g. loaded from data that
  // predates this component), keep it selectable instead of silently losing it.
  const allOptions = useMemo(() => {
    if (!value || options.some(o => o.value === value)) return options;
    const [h, min] = value.split(":").map(Number);
    const period = h < 12 ? "AM" : "PM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    const label = `${h12}:${String(min).padStart(2, "0")} ${period}`;
    return [...options, { value, label }].sort((a, b) => a.value.localeCompare(b.value));
  }, [options, value]);

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={className}>
        <SelectValue placeholder="Select time" />
      </SelectTrigger>
      <SelectContent className="max-h-64">
        {allOptions.map(o => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
